<svelte:options runes={false} />
<script>
  // The drift table (TASK-K2T-8): the first surface that joins the ledger-derived position, its mark
  // (+provenance), and the strategy's own target weight. Sorted biggest-position-first; drift carries
  // an explicit sign (never color alone); every unknown renders as — (honest absence).
  import { sortBook, fmtWeight, fmtDrift, displaySymbol as display } from '../../../lib/integrity.js';
  import { formatNum } from '../../../lib/lineage.js';

  export let book = [];
  /** The server's ticket deadband — drift at/inside it renders muted, past it highlighted. No client
   * default: undefined simply disables the highlight (never a second copy of server policy). */
  export let deadbandWeight = undefined;

  $: rows = sortBook(book);
  function fmtTime(t) {
    if (!t) return '—';
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }
</script>

{#if rows.length === 0}
  <div class="empty-state"><p class="empty-state-description">No positions and no book targets — the book is flat.</p></div>
{:else}
  <div class="it-book-scroll">
    <table>
      <thead>
        <tr>
          <th>Symbol</th><th class="num">Qty</th><th class="num">Avg entry</th><th class="num">Mark</th>
          <th class="num">Value</th><th class="num">Live wt</th><th class="num">Target wt</th><th class="num">Drift</th>
        </tr>
      </thead>
      <tbody>
        {#each rows as r (r.symbol)}
          <tr>
            <td>{display(r.symbol)}</td>
            <td class="num">{r.qty !== 0 ? formatNum(r.qty, 8) : '—'}</td>
            <td class="num">{r.qty !== 0 ? formatNum(r.avgEntry) : '—'}</td>
            <td class="num">
              {#if r.mark != null}
                {formatNum(r.mark)}<sup class="it-mark-src" title={`mark source: ${r.markSource ?? 'unknown'} · ${fmtTime(r.markTime)}`}>{(r.markSource ?? '?')[0]}</sup>
              {:else}—{/if}
            </td>
            <td class="num">{r.value != null ? formatNum(r.value, 2) : '—'}</td>
            <td class="num">{fmtWeight(r.liveWeight)}</td>
            <td class="num">{fmtWeight(r.targetWeight)}</td>
            <td class="num it-drift" class:it-drift-hot={r.driftWeight != null && deadbandWeight != null && Math.abs(r.driftWeight) > deadbandWeight}>
              {fmtDrift(r.driftWeight)}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<style>
  .it-book-scroll { max-height: 420px; overflow: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  th, td { padding: 0.32rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left; }
  th { position: sticky; top: 0; background: var(--surface-1, #14151a); font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .it-mark-src { font-size: 0.55rem; color: var(--text-muted, #8a8b8d); margin-left: 2px; cursor: help; }
  .it-drift { color: var(--text-muted, #8a8b8d); }
  .it-drift-hot { color: var(--warning, #d8a000); font-weight: 700; }
</style>
